import { z } from 'zod';
import { CANDIDATE_EVENT_KINDS, CANDIDATE_FILE_KINDS, CANDIDATE_MOVE_STAGES, CANDIDATE_STAGES, EMPLOYMENT_TYPES, OPENING_STATUSES, ROLES, STAFF_ROLES } from '@bulk/types';
import { dateOnlySchema, dateTimeSchema, idSchema, patchOf, searchQuerySchema } from './common';

export const RECRUITING_BOARD_LIMIT = 50;
export const MAX_RETENTION_MONTHS = 60;

const localizedSchema = z.object({ en: z.string(), ka: z.string() });

const openingTitleSchema = z
  .object({ en: z.string().trim().max(120).default(''), ka: z.string().trim().max(120).default('') })
  .refine((t) => t.en !== '' || t.ka !== '', { message: 'Give the title in at least one language' });

const openingDescriptionSchema = z.object({ en: z.string().trim().max(8000).default(''), ka: z.string().trim().max(8000).default('') });

export const recruitingSettingsSchema = z.object({
  retentionMonths: z
    .number()
    .int()
    .min(1)
    .max(MAX_RETENTION_MONTHS)
    .default(6)
    .describe('How many months a candidate and their CV are kept after being turned down, or after their opening closed while they were still applied or in screening, before the daily job deletes them'),
});
export type RecruitingSettings = z.infer<typeof recruitingSettingsSchema>;
export const updateRecruitingSettingsSchema = patchOf(recruitingSettingsSchema);
export type UpdateRecruitingSettingsInput = z.infer<typeof updateRecruitingSettingsSchema>;

export const createJobOpeningSchema = z.object({
  title: openingTitleSchema,
  description: openingDescriptionSchema.default({ en: '', ka: '' }),
  locationId: idSchema.nullable().default(null).describe('The branch the job is at; null means any branch'),
  departmentId: idSchema.nullable().default(null),
  employmentType: z.enum(EMPLOYMENT_TYPES).default('FULL_TIME'),
  status: z.enum(OPENING_STATUSES).default('OPEN'),
});
export type CreateJobOpeningInput = z.infer<typeof createJobOpeningSchema>;

export const updateJobOpeningSchema = patchOf(createJobOpeningSchema);
export type UpdateJobOpeningInput = z.infer<typeof updateJobOpeningSchema>;

export const jobOpeningListQuerySchema = z.object({
  status: z.enum(OPENING_STATUSES).optional(),
});
export type JobOpeningListQuery = z.infer<typeof jobOpeningListQuerySchema>;

export const jobOpeningSchema = z.object({
  id: idSchema,
  title: localizedSchema,
  description: localizedSchema,
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  departmentId: idSchema.nullable(),
  departmentName: z.string().nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  status: z.enum(OPENING_STATUSES),
  closedAt: z.string().nullable(),
  candidates: z.number().int().describe('Everyone who applied and is still on file'),
  inProgress: z.number().int().describe('Candidates not yet hired or rejected'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type JobOpeningDto = z.infer<typeof jobOpeningSchema>;

export const publicJobOpeningSchema = z.object({
  id: idSchema,
  title: localizedSchema,
  description: localizedSchema,
  locationName: z.string().nullable(),
  department: z.record(z.string(), z.string()).nullable().describe('The department’s name per locale, English under "en"'),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  postedAt: z.string(),
  retentionMonths: z.number().int().describe('How long the gym keeps an application after it is turned down or the opening closes, shown on the form'),
});
export type PublicJobOpeningDto = z.infer<typeof publicJobOpeningSchema>;

export const applyToOpeningSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.email().toLowerCase(),
  phone: z.string().trim().max(40).optional(),
  message: z.string().trim().max(2000).optional(),
  consent: z.stringbool().refine((agreed) => agreed, { message: 'Agree to the gym keeping your data to apply' }),
});
export type ApplyToOpeningInput = z.infer<typeof applyToOpeningSchema>;

export const candidateStageSchema = z.enum(CANDIDATE_STAGES);

export const candidateBoardQuerySchema = z.object({
  openingId: idSchema.optional(),
  q: z.string().trim().max(120).optional(),
});
export type CandidateBoardQuery = z.infer<typeof candidateBoardQuerySchema>;

export const candidateListQuerySchema = searchQuerySchema.extend({
  openingId: idSchema.optional(),
  stage: z.enum(CANDIDATE_STAGES).optional(),
});
export type CandidateListQuery = z.infer<typeof candidateListQuerySchema>;

export const candidateInterviewerSchema = z.object({
  id: idSchema.describe('The staff member’s place at this gym'),
  userId: idSchema,
  name: z.string(),
  role: z.enum(ROLES),
});
export type CandidateInterviewerDto = z.infer<typeof candidateInterviewerSchema>;

export const candidateSchema = z.object({
  id: idSchema,
  openingId: idSchema,
  openingTitle: localizedSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string(),
  stage: candidateStageSchema,
  stageChangedAt: z.string(),
  interviewAt: z.string().nullable(),
  interviewer: candidateInterviewerSchema.nullable(),
  rejectionReason: z.string().nullable(),
  employeeId: idSchema.nullable(),
  ratingAverage: z.number().nullable().describe('The mean of each rater’s latest rating, 1 to 5; null until someone rates'),
  ratingCount: z.number().int(),
  hasCv: z.boolean(),
  appliedAt: z.string(),
  retainedUntil: z.string().nullable().describe('When the daily job deletes the candidate and the CV: for a rejected one, and for one still applied or in screening whose opening is closed'),
});
export type CandidateDto = z.infer<typeof candidateSchema>;

export const candidateBoardSchema = z.object({
  columns: z.array(z.object({ stage: candidateStageSchema, total: z.number().int(), items: z.array(candidateSchema) })),
  limit: z.number().int(),
});
export type CandidateBoardDto = z.infer<typeof candidateBoardSchema>;

export const candidateFileSchema = z.object({
  id: idSchema,
  kind: z.enum(CANDIDATE_FILE_KINDS),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  createdAt: z.string(),
});
export type CandidateFileDto = z.infer<typeof candidateFileSchema>;

export const candidateFileLinkSchema = z.object({ url: z.string(), fileName: z.string(), mimeType: z.string() });
export type CandidateFileLinkDto = z.infer<typeof candidateFileLinkSchema>;

export const candidateEventSchema = z.object({
  id: idSchema,
  kind: z.enum(CANDIDATE_EVENT_KINDS),
  fromStage: candidateStageSchema.nullable(),
  toStage: candidateStageSchema.nullable(),
  text: z.string().nullable(),
  rating: z.number().int().nullable(),
  interviewAt: z.string().nullable(),
  interviewerName: z.string().nullable(),
  actorName: z.string().nullable().describe('Null for the candidate’s own application'),
  createdAt: z.string(),
});
export type CandidateEventDto = z.infer<typeof candidateEventSchema>;

export const hirePrefillSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  position: z.string().describe('The opening’s title in the gym’s language'),
  departmentId: idSchema.nullable(),
  locationId: idSchema.nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES),
});
export type HirePrefillDto = z.infer<typeof hirePrefillSchema>;

export const candidateDetailSchema = candidateSchema.extend({
  message: z.string().nullable(),
  hirePrefill: hirePrefillSchema,
  consentedAt: z.string(),
  files: z.array(candidateFileSchema),
  events: z.array(candidateEventSchema),
  myRating: z.number().int().nullable(),
});
export type CandidateDetailDto = z.infer<typeof candidateDetailSchema>;

export const moveCandidateSchema = z.object({
  stage: z.enum(CANDIDATE_MOVE_STAGES),
  interviewAt: dateTimeSchema.optional().describe('When the interview is; needed to move to INTERVIEW unless one is already set'),
  interviewerGymUserId: idSchema.nullable().optional(),
  reason: z.string().trim().min(1).max(300).optional().describe('Why a candidate is rejected; required to move to REJECTED'),
});
export type MoveCandidateInput = z.infer<typeof moveCandidateSchema>;

export const candidateNoteSchema = z.object({ text: z.string().trim().min(1).max(2000) });
export type CandidateNoteInput = z.infer<typeof candidateNoteSchema>;

export const candidateRatingSchema = z.object({
  rating: z.number().int().min(1).max(5),
  text: z.string().trim().max(2000).optional(),
});
export type CandidateRatingInput = z.infer<typeof candidateRatingSchema>;

export const hireCandidateSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.email().toLowerCase().nullable().optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  position: z.string().trim().max(80).nullable().optional(),
  departmentId: idSchema.nullable().optional(),
  locationId: idSchema.nullable().optional(),
  employmentType: z.enum(EMPLOYMENT_TYPES).default('FULL_TIME'),
  employmentStartDate: dateOnlySchema,
  invite: z
    .object({
      role: z.enum(STAFF_ROLES),
      locationIds: z.array(idSchema).max(100).default([]).describe('The branches they work at: at least one, except for owners and admins'),
    })
    .optional()
    .describe('Also invite the new employee to sign in to the gym with this role (needs users.manage)'),
});
export type HireCandidateInput = z.infer<typeof hireCandidateSchema>;

export const hireCandidateResultSchema = z.object({
  candidate: candidateDetailSchema,
  employeeId: idSchema,
  invitation: z
    .object({ acceptUrl: z.string().describe('Empty when the person already had an account here and was attached directly'), attachedExistingUser: z.boolean() })
    .nullable(),
});
export type HireCandidateResultDto = z.infer<typeof hireCandidateResultSchema>;
